import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";
import { Users, GraduationCap, Crown } from "lucide-react";
import { Panel, Row, Empty } from "./shared";

const pending = (arr) => arr.filter((a) => a.status === "pending");

export function ApprovalsTab() {
  const [mentorApps, setMentorApps] = useState([]);
  const [classReqs, setClassReqs] = useState([]);
  const [studentReqs, setStudentReqs] = useState([]);
  const [members, setMembers] = useState([]);

  const load = useCallback(() => {
    apiGet("/manage/pending-members").then(setMembers).catch(() => {});
    apiGet("/manage/mentor-applications").then(setMentorApps).catch(() => {});
    apiGet("/manage/class-mentor-requests").then(setClassReqs).catch(() => {});
    apiGet("/manage/student-mentor-requests").then(setStudentReqs).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (path, id, decision, label) => {
    try { await apiPost(`/manage/${path}/${id}/decide?decision=${decision}`, {}); toast.success(`${label} ${decision}d`); load(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="approvals-tab">
      <Panel title="Join requests (students & mentors)" accent="#FF5722" icon={Users} wide>
        {members.length === 0 && <Empty text="No pending join requests." />}
        {members.map((m) => (
          <Row key={m.id} testid={`member-req-${m.id}`}
            title={`${m.name} — wants to join as ${m.requested_role || "student"}`}
            sub={`${m.email}${m.organization_name ? " · " + m.organization_name : ""}`}
            onApprove={() => decide("pending-members", m.id, "approve", "Member")} onDeny={() => decide("pending-members", m.id, "deny", "Member")} />
        ))}
      </Panel>

      <Panel title="Mentor applications" accent="#31B8FF" icon={GraduationCap}>
        {pending(mentorApps).length === 0 && <Empty text="No pending mentor applications." />}
        {pending(mentorApps).map((a) => (
          <Row key={a.id} testid={`mentor-app-${a.id}`}
            title={a.user_name} sub={`${a.user_email}${a.organization_name ? " · " + a.organization_name : ""}`}
            onApprove={() => decide("mentor-applications", a.id, "approve", "Mentor")} onDeny={() => decide("mentor-applications", a.id, "deny", "Mentor")} />
        ))}
      </Panel>

      <Panel title="Become-a-mentor (per class)" accent="#B8FF3B" icon={Crown}>
        {pending(classReqs).length === 0 && <Empty text="No class-mentor requests." />}
        {pending(classReqs).map((r) => (
          <Row key={r.id} testid={`class-req-${r.id}`}
            title={r.user_name} sub={`Wants to mentor: ${r.class_title}`}
            onApprove={() => decide("class-mentor-requests", r.id, "approve", "Request")} onDeny={() => decide("class-mentor-requests", r.id, "deny", "Request")} />
        ))}
      </Panel>

      <Panel title="Student → mentor choices" accent="#FFD93D" icon={Users} wide>
        {studentReqs.length === 0 && <Empty text="No student mentor requests." />}
        {studentReqs.slice(0, 30).map((r) => (
          <div key={r.id} className="py-2 border-b border-[#ECE7D8] last:border-0 text-sm">
            <b>{r.student_name}</b> → {r.mentor_name} {r.class_title ? <span className="text-muted-foreground">({r.class_title})</span> : null}
          </div>
        ))}
      </Panel>
    </div>
  );
}
